// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { ApiError } from "./api";
import { isMessageKey, plural, t } from "./messages";
import type { CatalogState, CatalogSummary, CatalogType, SetupState } from "./types";

/** Resource types in the order the import writes them; unknown types follow, sorted. */
const COUNT_ORDER = ["products", "collections", "pages", "blogs", "articles", "policies", "metaobjects", "discounts", "menus", "files"];

/** "312 products" for one resource type. */
export function formatCount(type: string, count: number): string {
  const one = `count.${type}.one`;
  const other = `count.${type}.other`;
  if (isMessageKey(one) && isMessageKey(other)) return plural(count, one, other);
  return t("count.other", { count: count.toLocaleString("en"), type: type.replace(/_/g, " ") });
}

/** Import counts as display entries, known types first, empty types left out. */
export function countEntries(counts: Record<string, number>): { type: string; label: string }[] {
  const rank = (type: string) => {
    const i = COUNT_ORDER.indexOf(type);
    return i === -1 ? COUNT_ORDER.length : i;
  };
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([type, n]) => ({ type, label: formatCount(type, n) }));
}

/** "312 products, 12 collections". */
export function formatCounts(counts: Record<string, number>): string {
  return countEntries(counts).map((e) => e.label).join(t("count.separator"));
}

/** A date and time in the shop's time zone (the merchant's admin uses it too). */
export function formatDateTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short", timeZone }).format(date);
  } catch {
    // An unknown time zone name: fall back to the browser's.
    return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(date);
  }
}

/** "1.2 GB" (decimal units, like Granite's storage limits). */
export function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  return `${value.toLocaleString("en", { maximumFractionDigits: digits })} ${units[unit] ?? "B"}`;
}

/** Catalog types in display order, with the plural key their counts use ("product" → "products"). */
export const CATALOG_TYPES: readonly { type: CatalogType; plural: string }[] = [
  { type: "product", plural: "products" },
  { type: "collection", plural: "collections" },
  { type: "page", plural: "pages" },
  { type: "article", plural: "articles" },
  { type: "policy", plural: "policies" },
  { type: "metaobject", plural: "metaobjects" },
  { type: "discount", plural: "discounts" },
  { type: "menu", plural: "menus" },
  { type: "file", plural: "files" },
];

/** States whose resources have no note yet (waiting for their turn). */
const NO_NOTE: readonly string[] = ["deferred"] satisfies CatalogState[];

/** How many resources of one type the summary lists, in any state. */
export function summaryTotal(counts: CatalogSummary["counts"], type: CatalogType): number {
  return Object.values(counts[type] ?? {}).reduce((sum, n) => sum + n, 0);
}

/**
 * Notes per type from the catalog summary, keyed like the import counts ("products"): every
 * state except deferred has a note (archived and removed resources keep theirs).
 */
export function noteCounts(counts: CatalogSummary["counts"]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const { type, plural } of CATALOG_TYPES) {
    const byState = counts[type];
    if (!byState) continue;
    out[plural] = Object.entries(byState).reduce((sum, [state, n]) => (NO_NOTE.includes(state) ? sum : sum + n), 0);
  }
  return out;
}

export interface SetupProgress {
  done: number;
  total: number;
  complete: boolean;
}

/** Counts the setup guide's steps: connect Granite, import, open in Granite. */
export function setupProgress(setup: SetupState, connected: boolean): SetupProgress {
  const steps = [connected, setup.import_done, setup.opened_granite];
  const done = steps.filter(Boolean).length;
  return { done, total: steps.length, complete: done === steps.length };
}

/** The sentence shown for an error: our own words for known cases, the server's otherwise. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return t("error.network");
    if (error.status === 401) return t("error.session");
    return error.problem.detail ?? error.problem.title;
  }
  return t("error.unexpected");
}
